/**
 * Here we define the logic of the game.
 *
 * The game is a 16 by 16 grid that can be filled
 * with game objects.
 *
 * This state should be able to undo.
 *
 * Therefore we have a frozen game state.
 * On each input from the player we run all mutations and changes
 * as patches to the state.
 * Finally we apply all the patches to get a single diff from state_(n) to state_(n+1)
 */
import produce, { Patch, produceWithPatches, applyPatches } from "immer";
import { Dir } from "../entities/Player";
import { Entities, EntityKind, PlayerBullet, Crate } from "./entityKinds";
import { GameEvents, getId } from "../constants";
import { RulesPush } from "./rules/canBePushedOnto";
import { endOfTurnHook } from "./rules/endOfTurnHook";
import { GameTurnLogic } from "./GameTurnLogic";
import deepEqual from "deep-equal";

const getXYDiff = (x: number, y: number, dir: Dir) => {
  if (dir === Dir.Up) {
    return [x, y - 1];
  }
  if (dir === Dir.Down) {
    return [x, y + 1];
  }
  if (dir === Dir.Left) {
    return [x - 1, y];
  }
  if (dir === Dir.Right) {
    return [x + 1, y];
  }
  return [x, y];
};

export interface GameStateObj {
  player?: {
    x: number;
    y: number;
    dir: Dir;
  };
  board: Array<Array<Entities>>; // 64 length array. (store 2d into 1d)
}

export default class GameState {
  public gameState: GameStateObj;
  private turn_patches: Patch[];
  public turn_gameState: GameStateObj;
  private inverse_patches: Patch[][];
  public game_events: Phaser.Events.EventEmitter | null;
  public steady_state: boolean;
  public turn_counter: number;
  private addEntitiesMethods: Array<() => void>;
  private gameLogic: GameTurnLogic | null;
  constructor(overrideState?: GameState) {
    this.gameState = overrideState?.gameState || {
      player: undefined,
      board: Array.from(Array(16 * 16)).fill([]),
    };
    this.turn_gameState = this.gameState;
    this.turn_patches = [];
    this.inverse_patches = [];
    this.game_events = null;
    this.steady_state = true;
    this.turn_counter = 1;
    this.addEntitiesMethods = [];
    this.gameLogic = null;
  }

  silentMutateGameState(mutator: (state: GameStateObj) => void) {
    if (this.turn_patches.length > 0 || this.inverse_patches.length > 0) {
      throw new Error(
        "You can only silently mutate the game state before any undo state exists."
      );
    }
    this.gameState = produce(this.gameState, mutator);
    this.turn_gameState = this.gameState;
  }

  // Mutates the game state and keeps track.
  mutateGameState(mutator: (state: GameStateObj) => void, silent?: boolean) {
    const [nextState, patches, _] = produceWithPatches(
      this.turn_gameState,
      mutator
    );
    if (patches.length === 0) {
      return;
    }
    this.turn_gameState = nextState;
    this.turn_patches.push(...patches);
    if (!silent) {
      this.steady_state = false;
    }
  }

  setFinalStatesFromSteadyState() {
    endOfTurnHook(this.game_events as any, this, this.gameLogic as any);
  }

  // After a player interaction we run this function to move the
  // world forward.
  gameTurnLoopPostPlayerInteraction() {
    this.gameLogic = new GameTurnLogic(this);
    this.gameLogic.afterPlayerLogic();
  }

  // Queues up adding entities to be fired once overlaps are completed.
  addEntities(entity: Entities, x: number, y: number): boolean {
    if (x < 0 || x > 15 || y < 0 || y > 15) {
      return false;
    }

    this.addEntitiesMethods.push(() =>
      this.mutateGameState((state) => {
        const idx = getXYindex(x, y);
        // Don't add explosions or damage onto static walls, or other explosions.
        if (
          entity.kind === EntityKind.TDamage &&
          state.board[idx].some(
            ({ kind }) =>
              kind === EntityKind.StaticWall || kind === EntityKind.TDamage
          )
        ) {
          return;
        }

        state.board[idx].push(entity);
        if (entity.kind === EntityKind.TDamage) {
          this.game_events?.emit(GameEvents.FX_Explosion, x, y);
        }
      })
    );
    return true;
  }

  _addEntities() {
    for (const fn of this.addEntitiesMethods) {
      fn();
    }
    this.addEntitiesMethods = [];
  }

  addBulletFromPool_aux(
    x: number,
    y: number,
    dir: Dir,
    state: GameStateObj
  ): boolean {
    let hitSolid = false;
    let [x2, y2] = [x, y];
    let [x3, y3] = [x, y];
    let endDir = dir;
    while (!hitSolid) {
      [x3, y3] = [x2, y2];
      let idx = getXYindex(x2, y2);
      // Fixes rail gun shooting off the map
      if (x2 < 0 || x2 > 15 || y2 < 0 || y2 > 15) {
        break;
      }
      hitSolid = state.board[idx].some(
        (e) =>
          RulesPush.canBePushedOnto(e) !== RulesPush.OverlapResolution.CanMove
      );
      const bullet: PlayerBullet = {
        id: getId(),
        kind: EntityKind.PlayerBullet,
        x: x2,
        y: y2,
        dir: dir,
      };
      state.board[idx].push(bullet);
      [x2, y2] = getXYDiff(x2, y2, dir);

      const reflector: Crate | undefined = state.board[idx].find(
        (e) => e.kind === EntityKind.Crate && e.reflects
      );
      if (reflector) {
        reflector.entity?.().reflectSFX();
        if (reflector.reflects === "/") {
          if (dir === Dir.Right) {
            endDir = Dir.Up;
            this.addBulletFromPool_aux(x3, y3 - 1, Dir.Up, state);
          } else if (dir === Dir.Left) {
            endDir = Dir.Down;
            this.addBulletFromPool_aux(x3, y3 + 1, Dir.Down, state);
          } else if (dir === Dir.Down) {
            endDir = Dir.Left;
            this.addBulletFromPool_aux(x3 - 1, y3, Dir.Left, state);
          } else if (dir === Dir.Up) {
            endDir = Dir.Right;
            this.addBulletFromPool_aux(x3 + 1, y3, Dir.Right, state);
          }
        }
        if (reflector.reflects === "\\") {
          if (dir === Dir.Right) {
            endDir = Dir.Down;
            this.addBulletFromPool_aux(x3, y3 + 1, Dir.Down, state);
          } else if (dir === Dir.Left) {
            endDir = Dir.Up;
            this.addBulletFromPool_aux(x3, y3 - 1, Dir.Up, state);
          } else if (dir === Dir.Down) {
            endDir = Dir.Right;
            this.addBulletFromPool_aux(x3 + 1, y3, Dir.Right, state);
          } else if (dir === Dir.Up) {
            endDir = Dir.Left;
            this.addBulletFromPool_aux(x3 - 1, y3, Dir.Left, state);
          }
        }
      }
    }

    this.game_events?.emit(GameEvents.PlayerShot, {
      x,
      y,
      x2: x3,
      y2: y3,
      dir,
      endDir,
    });
  }
  addBulletFromPool(x: number, y: number, dir: Dir) {
    this.mutateGameState((state: GameStateObj) => {
      this.addBulletFromPool_aux(x, y, dir, state);
    });
  }
  static moveEntityOnBoard(
    state: GameStateObj,
    entity: Entities,
    idx: number,
    fromIdx?: number
  ) {
    if (fromIdx !== undefined) {
      const place = state.board[fromIdx].findIndex((e) => e.id === entity.id);
      if (place === -1) {
        throw new Error("lost track of last index...");
      }
      state.board[fromIdx].splice(place, 1);
    }
    state.board[idx].push(entity);
  }

  // Must be called once all systems have mutated game state.
  // Squashes patches making it easy to undo a state change.
  finalizeGameTurn() {
    this.game_events?.emit(GameEvents.TurnFinished);
    if (this.turn_patches.length === 0) {
      return;
    }

    const [nextState, _, reversePatch] = produceWithPatches(
      this.gameState,
      (draft) => {
        applyPatches(draft, this.turn_patches);
      }
    );
    if (!deepEqual(this.turn_gameState, this.gameState)) {
      this.inverse_patches.push(reversePatch);
      this.turn_counter += 1;
      this.gameState = nextState;
      this.turn_gameState = this.gameState;
    }
    this.turn_patches = [];

    // Used for visual effects.

    console.groupEnd();

    console.group(`Turn ${this.turn_counter}`);
  }

  undoState(): boolean {
    if (this.inverse_patches.length == 0) {
      return false;
    }
    const reversePatches = this.inverse_patches.pop();
    if (reversePatches === undefined) {
      return false;
    }
    const [nextState] = produceWithPatches(this.gameState, (draft) => {
      applyPatches(draft, reversePatches);
    });
    this.gameState = nextState;
    this.turn_gameState = nextState;
    const entitiesToUndo = this.gameState.board.flat();
    entitiesToUndo.forEach((entity) => {
      if (!entity || entity.kind === EntityKind.Empty) {
        return;
      }
      if (entity.kind === EntityKind.Crate) {
        entity.entity?.().undo(entity);
      } else if (entity.kind === EntityKind.PlayerBullet) {
        //nothing
      } else if (entity.kind === EntityKind.StaticWall) {
        // Do nothing
      } else if (entity.kind === EntityKind.Door) {
        entity.entity?.().undo(entity);
      } else if (entity.kind === EntityKind.MeleeEnemy) {
        entity.entity?.().undo(entity);
      } else if (entity.kind === EntityKind.ExtendedBlock) {
        entity.entity?.().undo(entity);
      } else {
        console.warn("didn't undo:", entity);
      }
    });
    return true;
  }
}

export enum PlayerMoves {
  FaceLeft = 0,
  FaceRight,
  FaceDown,
  FaceUp,
  MoveLeft,
  MoveRight,
  MoveDown,
  MoveUp,
  ShootLeft,
  ShootRight,
  ShootDown,
  ShootUp,
}

export function getXYindex(x: number, y: number) {
  return x + y * 16;
}
